/**
 * DOM overlay для чёткого UI поверх Phaser (Retina).
 * Singleton: showHub / hide, без сторонних библиотек.
 * Не импортируем GameState, чтобы избежать циклических зависимостей при загрузке сцен.
 */

import { resolvePublicAssetPath } from '../utils/paths';
import { createCard, createIconButton, createPackCard, createPill, createPillButton } from '../ui/components';
import { soundManager } from './SoundManager';

const UI_ROOT_ID = 'ui-root';

const LAZY_IMAGE_STYLES = {
  wrap: 'position:relative;overflow:hidden;border-radius:12px;background:#444;width:120px;height:120px;flex-shrink:0',
  placeholder: 'position:absolute;inset:0;background:#444',
  img: 'width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s',
  imgLoaded: 'opacity:1',
  error: 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:#444;color:#888;font-size:0.85rem;padding:4px',
};

export interface CreateLazyImageOptions {
  src: string;
  alt: string;
  className?: string;
  /** true = поставить src сразу (для Daily, одна картинка). */
  loadImmediately?: boolean;
}

/**
 * Создаёт обёртку с img (без src, url в data-src), loading="lazy", decoding="async",
 * заглушка #444, border-radius 12px. При load — класс "loaded", при error — "Ошибка загрузки".
 */
export function createLazyImage(options: CreateLazyImageOptions): HTMLElement {
  const { src, alt, className = '', loadImmediately = false } = options;
  const url = src ? resolvePublicAssetPath(src) : '';

  const wrap = document.createElement('div');
  wrap.className = `lazy-image-wrap ${className}`.trim();
  wrap.style.cssText = LAZY_IMAGE_STYLES.wrap;
  wrap.dataset.lazyWrap = '1';

  const placeholder = document.createElement('div');
  placeholder.className = 'lazy-image-placeholder';
  placeholder.style.cssText = LAZY_IMAGE_STYLES.placeholder;
  placeholder.setAttribute('aria-hidden', 'true');
  wrap.appendChild(placeholder);

  const img = document.createElement('img');
  img.alt = alt;
  img.loading = 'lazy';
  img.decoding = 'async';
  img.style.cssText = LAZY_IMAGE_STYLES.img;
  img.dataset.lazyImg = '1';
  if (url) {
    if (loadImmediately) {
      img.src = url;
    } else {
      img.dataset.src = url;
    }
  }
  wrap.appendChild(img);

  const errorEl = document.createElement('div');
  errorEl.className = 'lazy-image-error';
  errorEl.style.cssText = LAZY_IMAGE_STYLES.error;
  errorEl.style.display = 'none';
  errorEl.textContent = 'Ошибка загрузки';
  wrap.appendChild(errorEl);

  function onLoad(): void {
    wrap.classList.add('loaded');
    placeholder.style.display = 'none';
    img.style.opacity = '1';
    errorEl.style.display = 'none';
  }
  function onError(): void {
    placeholder.style.display = 'none';
    img.style.display = 'none';
    errorEl.style.display = 'flex';
  }

  img.addEventListener('load', onLoad);
  img.addEventListener('error', onError);
  if (loadImmediately && url) {
    if (img.complete) onLoad();
  }

  return wrap;
}

/** Запускает IntersectionObserver: при появлении в viewport ставит img.src = data-src. */
export function setupLazyImages(container: HTMLElement): void {
  const images = container.querySelectorAll<HTMLImageElement>('img[data-src]');
  if (images.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const img = entry.target as HTMLImageElement;
        const src = img.dataset.src;
        if (src) {
          img.src = src;
          img.removeAttribute('data-src');
          observer.unobserve(img);
        }
      });
    },
    { rootMargin: '100px', threshold: 0.01 }
  );

  images.forEach((img) => observer.observe(img));
}

const DAILY_SECTION_TITLE = 'Пазл дня';
const DAILY_UNAVAILABLE_TEXT = 'Пазл дня недоступен';
const DAILY_COUNTDOWN_PREFIX = 'Сменится через: ';

/** Создаёт блок daily: dailyTitle + dailyCard (Card с inner). Возвращает контейнер. */
function createDailySection(
  hubView: HubViewData,
  callbacks: HubCallbacks
): { dailySection: HTMLElement } {
  const dailySection = document.createElement('div');
  dailySection.id = 'hub-daily-section';
  dailySection.style.cssText =
    `display:flex;flex-direction:column;align-items:center;width:100%;gap:${HUB_LAYOUT.dailySectionGapPx}px`;

  const { card: dailyCard, inner: dailyCardInner } = createCard({
    gapPx: HUB_LAYOUT.dailyCardGapPx,
  });
  dailyCard.id = 'hub-daily-card';

  const dailyTitle = document.createElement('h2');
  dailyTitle.id = 'hub-daily-title';
  dailyTitle.className = 'ui-daily-card-title';
  dailyTitle.textContent = DAILY_SECTION_TITLE;
  dailyCardInner.appendChild(dailyTitle);

  const dailyPreviewWrap = document.createElement('div');
  dailyPreviewWrap.id = 'hub-daily-preview-wrap';
  dailyPreviewWrap.className = 'ui-daily-preview-wrap';
  dailyPreviewWrap.style.cssText =
    'width:70%;flex-shrink:0;overflow:hidden;border-radius:var(--ui-preview-radius, 12px);background:var(--ui-pill-background, #333)';
  dailyPreviewWrap.dataset.dailyPreview = '1';
  if (hubView.dailyCard.imageUrl) {
    const dailyPreview = createLazyImage({
      src: hubView.dailyCard.imageUrl,
      alt: hubView.dailyCard.title,
      className: 'ui-daily-preview',
      loadImmediately: true,
    });
    const imgWrap = dailyPreview as HTMLElement;
    imgWrap.style.width = '100%';
    imgWrap.style.height = '100%';
    const img = imgWrap.querySelector('img');
    if (img) img.style.objectFit = 'cover';
    dailyPreviewWrap.appendChild(dailyPreview);
  }
  dailyCardInner.appendChild(dailyPreviewWrap);

  dailyCardInner.style.gap = 'var(--ui-gap-sm)';

  const statusRow = document.createElement('div');
  statusRow.className = 'ui-daily-status-row';
  statusRow.style.cssText =
    'display:flex;align-items:center;justify-content:space-between;width:100%;gap:0.5rem;min-height:0';

  const { progressCollected, progressTotal } = hubView.dailyCard;
  const progressRow = document.createElement('div');
  progressRow.className = 'ui-daily-progress-row';
  const barTrack = document.createElement('div');
  barTrack.className = 'ui-daily-progress-track';
  const barFill = document.createElement('div');
  barFill.id = 'hub-daily-progress-fill';
  barFill.className = 'ui-daily-progress-fill';
  barFill.style.width = `${progressTotal > 0 ? (progressCollected / progressTotal) * 100 : 0}%`;
  barTrack.appendChild(barFill);
  progressRow.appendChild(barTrack);
  const progressLabel = document.createElement('span');
  progressLabel.id = 'hub-daily-progress-label';
  progressLabel.className = 'ui-daily-progress-label';
  progressLabel.textContent = `${progressCollected}/${progressTotal}`;
  progressRow.appendChild(progressLabel);
  statusRow.appendChild(progressRow);

  const dailyCountdownText = document.createElement('span');
  dailyCountdownText.id = 'hub-daily-countdown';
  dailyCountdownText.className = 'ui-daily-countdown';
  dailyCountdownText.style.cssText = 'margin:0;white-space:nowrap';
  dailyCountdownText.textContent = '';
  if (hubView.dailyCard.status === 'expired') dailyCountdownText.style.display = 'none';
  statusRow.appendChild(dailyCountdownText);

  dailyCardInner.appendChild(statusRow);

  const showDailyButton = hubView.dailyCard.status !== 'expired';
  if (showDailyButton) {
    let label: string;
    let onClick: () => void;
    if (hubView.dailyCard.status === 'new') {
      label = 'Начать пазл';
      onClick = callbacks.onStartDaily;
    } else if (hubView.dailyCard.status === 'collected') {
      label = 'Пересобрать';
      onClick = callbacks.onStartDaily;
    } else {
      label = 'Продолжить пазл';
      onClick = callbacks.onContinueDaily;
    }
    const dailyCTAButton = createPillButton({
      text: label,
      onClick,
      ariaLabel: label,
    });
    dailyCTAButton.id = 'hub-daily-cta';
    dailyCTAButton.classList.add('hub-daily-cta');
    dailyCTAButton.style.width = '100%';
    dailyCTAButton.style.justifyContent = 'center';
    dailyCTAButton.style.boxSizing = 'border-box';
    dailyCardInner.appendChild(dailyCTAButton);
  }

  dailySection.appendChild(dailyCard);

  return { dailySection };
}

type PackRow = HubViewData['packs'][number];

const HUB_PACKS_PREVIEW_LIMIT = 4;

/** Создаёт блок «Подборки»: кликабельный заголовок + packsGrid (макс 10) + кнопка «Смотреть все». */
function createPacksSection(
  packs: PackRow[],
  callbacks: Pick<HubCallbacks, 'onOpenPack' | 'onGoPacksList'>
): { packsSection: HTMLElement; packsGrid: HTMLElement } {
  const packsSection = document.createElement('div');
  packsSection.id = 'hub-packs-section';
  packsSection.style.cssText =
    `display:flex;flex-direction:column;align-items:center;width:100%;gap:${HUB_LAYOUT.packsSectionGapPx}px`;

  const packsTitle = document.createElement('h2');
  packsTitle.id = 'hub-packs-title';
  packsTitle.className = 'ui-subtitle';
  packsTitle.textContent = 'Подборки';
  packsSection.appendChild(packsTitle);

  const packsGrid = document.createElement('div');
  packsGrid.id = 'hub-packs-grid';
  packsGrid.style.cssText =
    `display:grid;gap:${PACKS_GRID_GAP_PX}px;width:100%`;

  const packsToShow = packs.slice(0, HUB_PACKS_PREVIEW_LIMIT);
  packsToShow.forEach((pack) => {
    const preview = pack.firstPuzzleImageUrl
      ? createLazyImage({
          src: pack.firstPuzzleImageUrl,
          alt: pack.title,
          className: 'ui-pack-preview',
          loadImmediately: false,
        })
      : null;
    if (preview) {
      (preview as HTMLElement).style.width = '100%';
      (preview as HTMLElement).style.height = '100%';
      const img = (preview as HTMLElement).querySelector('img');
      if (img) img.style.objectFit = 'cover';
    }
    const card = createPackCard({
      preview,
      titleText: pack.title,
      progressCollected: pack.collectedInPack,
      progressTotal: pack.total,
      onClick: () => callbacks.onOpenPack(pack.packId),
      isPurchased: pack.isPurchased,
      lockOverlayText: pack.isPurchased ? undefined : '1 пазл бесплатно',
      rewardBadge: !pack.isPurchased && pack.hasRewardedPuzzle ? 'Ad' : undefined,
    });
    packsGrid.appendChild(card);
  });

  packsSection.appendChild(packsGrid);

  const btnAllPacks = createPillButton({
    text: 'Смотреть все подборки',
    onClick: () => callbacks.onGoPacksList(),
    ariaLabel: 'Смотреть все подборки',
  });
  btnAllPacks.classList.add('hub-daily-cta');
  btnAllPacks.style.width = '100%';
  btnAllPacks.style.justifyContent = 'center';
  btnAllPacks.style.boxSizing = 'border-box';
  packsSection.appendChild(btnAllPacks);

  return { packsSection, packsGrid };
}

/** Обновляет отображение блока daily по данным (статус, лейбл CTA). Вызывать при смене состояния без перестроения хаба. */
export function updateDailyCard(
  state: HubViewData['dailyCard'],
  callbacks: Pick<HubCallbacks, 'onStartDaily' | 'onContinueDaily'>
): void {
  const progressCollected = state.progressCollected ?? (state.status === 'collected' ? state.progressTotal ?? 1 : 0);
  const progressTotal = state.progressTotal ?? 1;
  const progressFillEl = document.getElementById('hub-daily-progress-fill');
  const progressLabelEl = document.getElementById('hub-daily-progress-label');
  if (progressFillEl) progressFillEl.style.width = `${progressTotal > 0 ? (progressCollected / progressTotal) * 100 : 0}%`;
  if (progressLabelEl) progressLabelEl.textContent = `${progressCollected}/${progressTotal}`;

  const ctaEl = document.getElementById('hub-daily-cta') as HTMLButtonElement | null;
  if (ctaEl) {
    const ctaTextEl = ctaEl.querySelector<HTMLElement>('.ui-pill-btn-text');
    const setCta = (text: string, handler: () => void) => {
      if (ctaTextEl) ctaTextEl.textContent = text;
      else ctaEl.textContent = text;
      ctaEl.style.display = '';
      ctaEl.onclick = () => {
        soundManager.playClick();
        handler();
      };
    };
    if (state.status === 'new') {
      setCta('Начать пазл', () => callbacks.onStartDaily());
    } else if (state.status === 'collected') {
      setCta('Пересобрать', () => callbacks.onStartDaily());
    } else if (state.status === 'inProgress') {
      setCta('Продолжить пазл', () => callbacks.onContinueDaily());
    } else {
      ctaEl.style.display = 'none';
    }
  }
  const countdownEl = document.getElementById('hub-daily-countdown');
  if (countdownEl) {
    countdownEl.style.display = state.status === 'expired' ? 'none' : '';
  }
}

/** Обновляет текст таймера "Сменится через: HH:MM:SS". Вызывать раз в секунду (например из Hub scene). */
export function updateDailyCountdown(): void {
  const el = document.getElementById('hub-daily-countdown');
  if (!el) return;
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  const ms = midnight.getTime() - now.getTime();
  if (ms <= 0) {
    el.textContent = DAILY_UNAVAILABLE_TEXT;
    return;
  }
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  el.textContent = `${DAILY_COUNTDOWN_PREFIX}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Данные для отрисовки Hub (совместимо с GameState.getHubView()). */
export interface HubViewData {
  coins: number;
  hasActivePuzzle: boolean;
  activePuzzleSource: 'tutorial' | 'daily' | 'pack' | 'collection' | null;
  dailyCard: {
    status: string;
    title: string;
    dailyKey: string;
    imageUrl: string;
    progressCollected: number;
    progressTotal: number;
  };
  packs: Array<{
    packId: string;
    title: string;
    collectedInPack: number;
    total: number;
    isPurchased: boolean;
    firstPuzzleImageUrl: string;
    hasRewardedPuzzle: boolean;
  }>;
}

function getRoot(): HTMLElement {
  const el = document.getElementById(UI_ROOT_ID);
  if (!el) throw new Error('UiOverlay: #ui-root not found');
  return el;
}

/** Общий оверлей-панель (dim + panel) в стиле экрана завершения пазла. */
function createOverlayPanel(options: {
  dimRect?: { left: number; top: number; width: number; height: number } | null;
  title: string;
  subtitle?: string;
  buttons: Array<{ text: string; primary: boolean; onClick: () => void; className?: string }>;
}): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'ui-overlay-wrap';

  const dim = document.createElement('div');
  dim.className = 'ui-complete-dim';
  if (options.dimRect) {
    dim.style.left = `${options.dimRect.left}px`;
    dim.style.top = `${options.dimRect.top}px`;
    dim.style.width = `${options.dimRect.width}px`;
    dim.style.height = `${options.dimRect.height}px`;
    dim.style.borderRadius = '14px';
  }
  wrapper.appendChild(dim);

  const panel = document.createElement('div');
  panel.className = 'ui-complete-panel';

  const titleEl = document.createElement('h1');
  titleEl.className = 'ui-complete-title';
  titleEl.textContent = options.title;
  panel.appendChild(titleEl);

  if (options.subtitle) {
    const sub = document.createElement('p');
    sub.className = 'ui-complete-subtitle';
    sub.textContent = options.subtitle;
    panel.appendChild(sub);
  }

  if (options.buttons.length > 0) {
    const actions = document.createElement('div');
    actions.className = 'ui-complete-actions';
    for (const btn of options.buttons) {
      const b = document.createElement('button');
      b.className = btn.primary ? 'ui-pill-btn ui-complete-btn-primary' : 'ui-pill-btn ui-complete-btn-secondary';
      if (btn.className) b.classList.add(btn.className);
      b.textContent = btn.text;
      b.type = 'button';
      b.addEventListener('click', btn.onClick);
      actions.appendChild(b);
    }
    panel.appendChild(actions);
  }
  wrapper.appendChild(panel);

  return wrapper;
}

function formatPieceCountLabel(count: number): string {
  return String(count);
}

const PACKS_GRID_COLS = 2;
const PACKS_GRID_GAP_PX = 14; // 12–16px
const PACKS_GRID_CARD_HEIGHT_RATIO = 1.05; // cardH = cardW * ratio (чуть выше, чтобы превью в подборках было длиннее)

/** Константы layout хаба: отступы, зазоры, размеры (без магических чисел в коде). */
const HUB_LAYOUT = {
  rootMarginVerticalPx: 20,
  rootMaxWidthCss: 'var(--ui-hub-screen-max, var(--ui-screen-max, 600px))',
  contentAreaGapPx: 20,
  dailySectionGapPx: 16,
  dailyCardGapPx: 16,
  dailyPreviewMaxSizePx: 200,
  /** previewH = min(contentW * dailyPreviewWidthRatio, dailyPreviewMaxHeightPx); на малых экранах — dailyPreviewMaxHeightSmallPx */
  dailyPreviewWidthRatio: 0.6,
  dailyPreviewMaxHeightPx: 260,
  dailyPreviewMaxHeightSmallPx: 220,
  dailyPreviewSmallScreenBreakpointPx: 380,
  dailyPreviewAspectRatio: 1,
  packsSectionGapPx: 12,
  bottomBarPaddingTopPx: 8,
  packCardPaddingPx: 8,
  packCardInnerGapPx: 6,
  packCardThumbHeightPx: 80,
} as const;

let hubPacksGridResizeObserver: ResizeObserver | null = null;

function updateHubPacksGridLayout(gridEl: HTMLElement): void {
  const width = gridEl.getBoundingClientRect().width;
  const cardW = (width - PACKS_GRID_GAP_PX) / PACKS_GRID_COLS;
  const cardH = cardW * PACKS_GRID_CARD_HEIGHT_RATIO;
  gridEl.style.gridTemplateColumns = `repeat(${PACKS_GRID_COLS}, ${cardW}px)`;
  gridEl.style.gridAutoRows = `${cardH}px`;
}

/** Рассчитывает размеры сетки подборок и возвращает высоту секции (для скролла). */
export function layoutPacksGrid(bounds: { width: number }, packsCount: number): { cardW: number; cardH: number; sectionHeight: number } {
  const cardW = (bounds.width - PACKS_GRID_GAP_PX) / PACKS_GRID_COLS;
  const cardH = cardW * PACKS_GRID_CARD_HEIGHT_RATIO;
  const rows = Math.ceil(packsCount / PACKS_GRID_COLS);
  const gridHeight = rows * cardH + (rows - 1) * PACKS_GRID_GAP_PX;
  const titleHeight = 32;
  const sectionHeight = titleHeight + HUB_LAYOUT.packsSectionGapPx + gridHeight;
  return { cardW, cardH, sectionHeight };
}

export interface HubCallbacks {
  onStartDaily: () => void;
  onContinueDaily: () => void;
  onOpenInCollection: () => void;
  onGoPacks: () => void;
  onGoPacksList: () => void;
  onGoCollection: () => void;
  onOpenPack: (packId: string) => void;
}

export const UiOverlay = {
  showPuzzleLoadingOverlay(data?: { title?: string; subtitle?: string }): void {
    const root = getRoot();
    const existing = root.querySelector<HTMLElement>('[data-puzzle-loading-overlay="1"]');
    if (existing) return;

    const wrapper = createOverlayPanel({
      title: data?.title ?? 'Загрузка пазла...',
      subtitle: data?.subtitle ?? 'Подготавливаем кусочки',
      buttons: [],
    });
    wrapper.dataset.puzzleLoadingOverlay = '1';
    wrapper.classList.add('ui-screen', 'ui-complete-screen');
    root.appendChild(wrapper);

    const dim = wrapper.querySelector('.ui-complete-dim')!;
    const panel = wrapper.querySelector('.ui-complete-panel')!;
    requestAnimationFrame(() => {
      dim.classList.add('is-visible');
      panel.classList.add('is-visible');
    });
  },

  hidePuzzleLoadingOverlay(): void {
    const root = getRoot();
    const existing = root.querySelector<HTMLElement>('[data-puzzle-loading-overlay="1"]');
    existing?.remove();
  },

  showHub(hubView: HubViewData, callbacks: HubCallbacks): void {
    const root = getRoot();
    root.innerHTML = '';

    // Корневой контейнер хаба: центрируется по viewport, учёт safe-area (отступы 16–24px + padding из #ui-root)
    const hubRoot = document.createElement('div');
    hubRoot.id = 'hub-root';
    hubRoot.className = 'ui-screen';
    hubRoot.style.cssText =
      `display:flex;flex-direction:column;align-items:center;width:100%;max-width:${HUB_LAYOUT.rootMaxWidthCss};flex:1;min-height:0;margin:${HUB_LAYOUT.rootMarginVerticalPx}px 0`;
    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.alignItems = 'center';
    root.style.justifyContent = 'center';
    root.style.minHeight = '100%';
    root.appendChild(hubRoot);

    // ─── topBar: лого слева | spacer | Коллекция | настройки | валюта ─
    const topBar = document.createElement('div');
    topBar.id = 'hub-top-bar';
    topBar.className = 'hub-topbar';
    topBar.style.cssText = 'width:100%;flex-shrink:0';
    const brandWrap = document.createElement('div');
    brandWrap.className = 'hub-topbar-brand';
    const logoImg = document.createElement('img');
    logoImg.src = '/assets/img/logo_with_text.png';
    logoImg.alt = '';
    logoImg.className = 'hub-topbar-logo';
    logoImg.setAttribute('aria-hidden', 'true');
    brandWrap.appendChild(logoImg);
    topBar.appendChild(brandWrap);
    const spacer = document.createElement('div');
    spacer.style.flex = '1';
    spacer.style.minWidth = '8px';
    topBar.appendChild(spacer);
    const btnCollection = createPillButton({
      icon: '📁',
      text: 'Коллекция',
      onClick: callbacks.onGoCollection,
      ariaLabel: 'Коллекция',
    });
    topBar.appendChild(btnCollection);
    const coinsPill = createPill({
      icon: '💎',
      text: String(hubView.coins),
      ariaLabel: 'Монеты',
      iconClass: 'puzzle-coins-icon',
      textClass: 'puzzle-coins',
    });
    coinsPill.classList.add('puzzle-coins-capsule');
    topBar.appendChild(coinsPill);
    hubRoot.appendChild(topBar);

    // ─── contentArea: вертикальный стек dailySection → packsSection, зазор 16–24px ─
    const contentArea = document.createElement('div');
    contentArea.id = 'hub-content-area';
    contentArea.style.cssText =
      `display:flex;flex-direction:column;align-items:center;width:100%;flex:1;min-height:0;overflow-y:auto;gap:${HUB_LAYOUT.contentAreaGapPx}px`;

    // ─── dailySection (createDailySection) ─────────────────────────────────
    const { dailySection } = createDailySection(hubView, callbacks);
    contentArea.appendChild(dailySection);

    // ─── packsSection (createPacksSection) ───────────────────────────────────
    const { packsSection, packsGrid } = createPacksSection(hubView.packs, callbacks);
    contentArea.appendChild(packsSection);
    setupLazyImages(contentArea);

    hubRoot.appendChild(contentArea);

    // ─── bottomBar: приклеен к низу, пустой (вход в коллекцию только из PillButton в шапке) ─
    const bottomBar = document.createElement('div');
    bottomBar.id = 'hub-bottom-bar';
    bottomBar.style.cssText =
      `display:flex;align-items:center;justify-content:center;width:100%;flex-shrink:0;padding-top:${HUB_LAYOUT.bottomBarPaddingTopPx}px`;
    hubRoot.appendChild(bottomBar);

    // packsGrid: cardW от ширины контейнера, 2 колонки, раскладка по (col, row) через grid
    if (hubPacksGridResizeObserver) {
      hubPacksGridResizeObserver.disconnect();
      hubPacksGridResizeObserver = null;
    }
    requestAnimationFrame(() => {
      updateHubPacksGridLayout(packsGrid);
    });
    hubPacksGridResizeObserver = new ResizeObserver(() => {
      updateHubPacksGridLayout(packsGrid);
    });
    hubPacksGridResizeObserver.observe(packsGrid);
  },

  /** Пересчитывает размеры/позиции hubRoot, topBar, contentArea, bottomBar, dailyCard, packsGrid. Вызывать при resize. */
  layoutHub(): void {
    const root = getRoot();
    const hubRoot = root.querySelector<HTMLElement>('#hub-root');
    if (!hubRoot) return;

    const topBar = root.querySelector<HTMLElement>('#hub-top-bar');
    const contentArea = root.querySelector<HTMLElement>('#hub-content-area');
    const bottomBar = root.querySelector<HTMLElement>('#hub-bottom-bar');
    const dailySection = root.querySelector<HTMLElement>('#hub-daily-section');
    const dailyCard = root.querySelector<HTMLElement>('#hub-daily-card');
    const packsGrid = root.querySelector<HTMLElement>('#hub-packs-grid');

    hubRoot.style.margin = `${HUB_LAYOUT.rootMarginVerticalPx}px 0`;
    hubRoot.style.maxWidth = HUB_LAYOUT.rootMaxWidthCss;

    if (topBar) {
      topBar.style.display = 'flex';
      topBar.style.alignItems = 'center';
      topBar.style.justifyContent = 'flex-start';
      topBar.style.width = '100%';
      topBar.style.flexShrink = '0';
    }
    if (contentArea) {
      contentArea.style.gap = `${HUB_LAYOUT.contentAreaGapPx}px`;
    }
    if (bottomBar) {
      bottomBar.style.paddingTop = `${HUB_LAYOUT.bottomBarPaddingTopPx}px`;
    }
    if (dailySection) {
      dailySection.style.gap = `${HUB_LAYOUT.dailySectionGapPx}px`;
    }
    if (dailyCard) {
      dailyCard.style.width = '100%';
      dailyCard.style.gap = `${HUB_LAYOUT.dailyCardGapPx}px`;
    }
    const dailyPreviewWrap = root.querySelector<HTMLElement>('#hub-daily-preview-wrap');
    if (dailyPreviewWrap) {
      const contentW = contentArea?.getBoundingClientRect().width ?? hubRoot?.getBoundingClientRect().width ?? 360;
      const isSmall = contentW < HUB_LAYOUT.dailyPreviewSmallScreenBreakpointPx;
      const maxH = isSmall ? HUB_LAYOUT.dailyPreviewMaxHeightSmallPx : HUB_LAYOUT.dailyPreviewMaxHeightPx;
      const previewH = Math.min(contentW * HUB_LAYOUT.dailyPreviewWidthRatio, maxH);
      dailyPreviewWrap.style.width = '70%';
      dailyPreviewWrap.style.maxHeight = `${previewH}px`;
      dailyPreviewWrap.style.aspectRatio = String(HUB_LAYOUT.dailyPreviewAspectRatio);
    }
    if (packsGrid) {
      packsGrid.style.gap = `${PACKS_GRID_GAP_PX}px`;
      updateHubPacksGridLayout(packsGrid);
    }
  },

  /** Данные для экрана подборки (совместимо с GameState.getPackView()). */
  showPack(
    data: {
      packId: string;
      title: string;
      coins: number;
      isPurchased: boolean;
      collectedInPack: number;
      total: number;
      puzzles: Array<{
        puzzleId: string;
        title?: string;
        orderInPack: number;
        status: string;
        imageUrl: string;
        allowedDifficulties: number[];
      }>;
      lockedPromoText: string;
    },
    callbacks: {
      onBack: () => void;
      onCardClick: (puzzleId: string, title?: string, status?: string) => void;
    }
  ): void {
    const root = getRoot();
    root.innerHTML = '';

    const screen = document.createElement('div');
    screen.className = 'ui-screen collection-screen';
    screen.style.overflowY = 'auto';
    screen.style.maxHeight = '100vh';

    const header = document.createElement('div');
    header.className = 'hub-topbar';
    header.style.width = '100%';
    header.style.flexShrink = '0';

    const btnBack = createIconButton({
      ariaLabel: 'Назад',
      iconOrText: '←',
      onClick: callbacks.onBack,
    });
    btnBack.className = 'ui-btn ui-btn-back';
    header.appendChild(btnBack);

    const spacer = document.createElement('div');
    spacer.style.flex = '1';
    spacer.style.minWidth = '8px';
    header.appendChild(spacer);

    const coinsPill = createPill({
      icon: '💎',
      text: String(data.coins),
      ariaLabel: 'Монеты',
      iconClass: 'puzzle-coins-icon',
      textClass: 'puzzle-coins',
    });
    coinsPill.classList.add('puzzle-coins-capsule');
    header.appendChild(coinsPill);
    screen.appendChild(header);

    const content = document.createElement('div');
    content.className = 'collection-content';

    const titleEl = document.createElement('h1');
    titleEl.className = 'collection-title';
    titleEl.textContent = data.title;
    content.appendChild(titleEl);

    const subtitleEl = document.createElement('p');
    subtitleEl.className = 'collection-subtitle';
    subtitleEl.textContent = `Собрано ${data.collectedInPack}/${data.total}`;
    content.appendChild(subtitleEl);

    const divider = document.createElement('div');
    divider.className = 'collection-divider';
    content.appendChild(divider);

    const grid = document.createElement('div');
    grid.style.display = 'grid';
    grid.style.gridTemplateColumns = 'repeat(auto-fill, minmax(160px, 1fr))';
    grid.style.gap = '1rem';
    grid.style.width = '100%';

    const BADGE_TEXT: Record<string, string> = {
      free: 'Бесплатно',
      rewardedLocked: 'За рекламу',
      rewardedUnlocked: 'Открыто',
      locked: 'Закрыто',
      availableOwned: 'Доступно',
    };
    data.puzzles.forEach((puzzle) => {
      const card = document.createElement('div');
      card.className = 'ui-pack-card';
      card.style.cssText =
        'display:flex;flex-direction:column;align-items:center;gap:0.35rem;padding:0.5rem;background:#333;border-radius:12px;text-align:center';

      const thumbWrap = createLazyImage({
        src: puzzle.imageUrl,
        alt: puzzle.title ?? puzzle.puzzleId,
        className: 'ui-pack-card-thumb',
        loadImmediately: false,
      });
      (thumbWrap as HTMLElement).style.width = '100%';
      (thumbWrap as HTMLElement).style.height = '100px';
      (thumbWrap as HTMLElement).style.minHeight = '100px';
      card.appendChild(thumbWrap);

      const nameEl = document.createElement('p');
      nameEl.className = 'ui-coins';
      nameEl.style.fontSize = '0.9rem';
      nameEl.style.margin = '0';
      nameEl.textContent = puzzle.title ?? puzzle.puzzleId;
      card.appendChild(nameEl);

      const badge = document.createElement('span');
      badge.style.cssText =
        'font-size:0.75rem;padding:2px 6px;border-radius:4px;background:#444;color:#ccc';
      badge.textContent = BADGE_TEXT[puzzle.status] ?? puzzle.status;
      card.appendChild(badge);

      if (puzzle.status === 'locked') {
        const promo = document.createElement('p');
        promo.className = 'ui-coins';
        promo.style.cssText = 'font-size:0.8rem;font-weight:700;color:#aaa;margin:0';
        promo.textContent = 'Купи подборку, чтобы открыть пазл';
        card.appendChild(promo);
      }

      card.addEventListener('click', () => {
        soundManager.playClick();
        callbacks.onCardClick(puzzle.puzzleId, puzzle.title, puzzle.status);
      });

      grid.appendChild(card);
    });

    content.appendChild(grid);
    screen.appendChild(content);
    setupLazyImages(screen);
    root.appendChild(screen);
  },

  /** Экран выбора сложности (итерация 3.4). */
  showDifficultySelect(
    data: { puzzleTitle?: string; difficulties: number[] },
    callbacks: { onSelect: (difficulty: number) => void; onBack: () => void }
  ): void {
    const root = getRoot();
    root.innerHTML = '';

    const screen = document.createElement('div');
    screen.className = 'ui-screen';

    const title = document.createElement('h1');
    title.className = 'ui-title';
    title.textContent = 'Выбери сложность';
    screen.appendChild(title);

    if (data.puzzleTitle) {
      const sub = document.createElement('p');
      sub.className = 'ui-coins';
      sub.textContent = data.puzzleTitle;
      screen.appendChild(sub);
    }

    data.difficulties.forEach((d) => {
      const btn = document.createElement('button');
      btn.className = 'ui-btn';
      btn.textContent = String(d);
      btn.type = 'button';
      btn.addEventListener('click', () => callbacks.onSelect(d));
      screen.appendChild(btn);
    });

    const btnBack = document.createElement('button');
    btnBack.className = 'ui-btn';
    btnBack.style.background = '#555';
    btnBack.textContent = 'Назад';
    btnBack.type = 'button';
    btnBack.addEventListener('click', callbacks.onBack);
    screen.appendChild(btnBack);

    root.appendChild(screen);
  },

  /** HUD экрана пазла: фиксированный topbar, side-кнопки, tray. Стабильный layout на десктопе и мобилке. */
  showPuzzleHud(
    model: { coins: number; pieceCount: number },
    callbacks: {
      onBack: () => void;
      onZoomOut?: () => void;
      onZoomIn?: () => void;
      getZoomPercent?: () => number;
    }
  ): void {
    const root = getRoot();
    root.innerHTML = '';

    const puzzleUi = document.createElement('div');
    puzzleUi.className = 'puzzle-ui ui-screen';

    const topbar = document.createElement('div');
    topbar.className = 'puzzle-topbar';
    topbar.dataset.puzzleTopbar = '1';

    const btnBack = createIconButton({
      ariaLabel: 'Назад',
      iconOrText: '←',
      onClick: callbacks.onBack,
    });
    btnBack.dataset.puzzleTopbarBack = '1';
    topbar.appendChild(btnBack);

    const logoBrand = document.createElement('div');
    logoBrand.className = 'hub-topbar-brand';
    logoBrand.dataset.puzzleTopbarLogo = '1';
    logoBrand.style.display = 'none';
    const logoImg = document.createElement('img');
    logoImg.src = '/assets/img/logo_with_text.png';
    logoImg.alt = '';
    logoImg.className = 'hub-topbar-logo';
    logoImg.setAttribute('aria-hidden', 'true');
    logoBrand.appendChild(logoImg);
    topbar.appendChild(logoBrand);

    const center = document.createElement('div');
    center.className = 'puzzle-topbar-center';
    const capsule = document.createElement('div');
    capsule.className = 'puzzle-progress-capsule';
    capsule.setAttribute('role', 'status');
    capsule.setAttribute('aria-label', 'Прогресс пазла');
    const barTrack = document.createElement('div');
    barTrack.className = 'puzzle-progress-bar';
    const barFill = document.createElement('div');
    barFill.className = 'puzzle-progress-bar-fill';
    barFill.style.width = '0%';
    barTrack.appendChild(barFill);
    capsule.appendChild(barTrack);
    const inner = document.createElement('div');
    inner.className = 'puzzle-progress-inner';
    const icon = document.createElement('span');
    icon.className = 'puzzle-progress-icon';
    icon.textContent = '🧩';
    icon.setAttribute('aria-hidden', 'true');
    inner.appendChild(icon);
    const progress = document.createElement('span');
    progress.className = 'puzzle-progress';
    progress.textContent = `0/${model.pieceCount}`;
    inner.appendChild(progress);
    capsule.appendChild(inner);
    center.appendChild(capsule);
    topbar.appendChild(center);

    const rightSlot = document.createElement('div');
    rightSlot.className = 'puzzle-topbar-right';
    rightSlot.dataset.puzzleTopbarRight = '1';

    const hasZoomCallbacks =
      typeof callbacks.onZoomOut === 'function' &&
      typeof callbacks.onZoomIn === 'function' &&
      typeof callbacks.getZoomPercent === 'function';
    if (hasZoomCallbacks) {
      const zoomWrap = document.createElement('div');
      zoomWrap.className = 'puzzle-zoom-controls';
      zoomWrap.dataset.puzzleTopbarZoom = '1';
      const formatPercent = (): string => `${Math.max(100, Math.round(callbacks.getZoomPercent?.() ?? 100))}%`;

      const zoomOutBtn = document.createElement('button');
      zoomOutBtn.type = 'button';
      zoomOutBtn.className = 'puzzle-zoom-btn';
      zoomOutBtn.textContent = '-';
      zoomOutBtn.setAttribute('aria-label', 'Уменьшить');
      zoomOutBtn.addEventListener('click', () => {
        soundManager.playClick();
        callbacks.onZoomOut?.();
        zoomLabel.textContent = formatPercent();
      });
      zoomWrap.appendChild(zoomOutBtn);

      const zoomLabel = document.createElement('span');
      zoomLabel.className = 'puzzle-zoom-label';
      zoomLabel.dataset.puzzleZoomLabel = '1';
      zoomLabel.textContent = formatPercent();
      zoomLabel.setAttribute('aria-live', 'polite');
      zoomWrap.appendChild(zoomLabel);

      const zoomInBtn = document.createElement('button');
      zoomInBtn.type = 'button';
      zoomInBtn.className = 'puzzle-zoom-btn';
      zoomInBtn.textContent = '+';
      zoomInBtn.setAttribute('aria-label', 'Увеличить');
      zoomInBtn.addEventListener('click', () => {
        soundManager.playClick();
        callbacks.onZoomIn?.();
        zoomLabel.textContent = formatPercent();
      });
      zoomWrap.appendChild(zoomInBtn);

      rightSlot.appendChild(zoomWrap);
    }

    const coinsCapsule = createPill({
      icon: '💎',
      text: String(model.coins),
      ariaLabel: 'Монеты',
      iconClass: 'puzzle-coins-icon',
      textClass: 'puzzle-coins',
    });
    coinsCapsule.classList.add('puzzle-coins-capsule');
    coinsCapsule.dataset.puzzleTopbarCoins = '1';
    rightSlot.appendChild(coinsCapsule);

    const skipPill = createPill({
      text: 'Пропустить',
      ariaLabel: 'Пропустить обучение',
    });
    skipPill.classList.add('puzzle-topbar-skip', 'clickable');
    skipPill.dataset.puzzleTopbarSkip = '1';
    skipPill.style.display = 'none';
    rightSlot.appendChild(skipPill);

    topbar.appendChild(rightSlot);

    puzzleUi.appendChild(topbar);

    const boardWrap = document.createElement('div');
    boardWrap.className = 'puzzle-board-wrap';
    boardWrap.dataset.puzzleBoardWrap = '1';
    puzzleUi.appendChild(boardWrap);

    const tray = document.createElement('div');
    tray.id = 'puzzle-tray';
    tray.className = 'puzzle-tray';
    tray.dataset.puzzleTray = '1';
    const trayScroll = document.createElement('div');
    trayScroll.className = 'puzzle-tray-scroll';
    tray.appendChild(trayScroll);
    puzzleUi.appendChild(tray);

    root.appendChild(puzzleUi);
  },

  /** Rect DOM-элемента лотка (getBoundingClientRect). */
  getTrayDomRect(): DOMRect | null {
    return document.getElementById('puzzle-tray')?.getBoundingClientRect() ?? null;
  },

  /** Rect внутренней области лотка (пунктирный блок .puzzle-tray-scroll). */
  getTrayScrollDomRect(): DOMRect | null {
    return document.querySelector('.puzzle-tray-scroll')?.getBoundingClientRect() ?? null;
  },

  /** Задать высоту области скролла лотка (px). Ограничиваем, чтобы нижняя граница оставалась на экране. Возвращает фактически установленную высоту (px). */
  setTrayScrollHeightPx(pixels: number): number {
    const el = document.querySelector('.puzzle-tray-scroll') as HTMLElement | null;
    if (!el) return pixels;
    const css = getComputedStyle(el);
    const minCss = Number.parseFloat(css.getPropertyValue('--puzzle-tray-scroll-min-h'));
    const maxCss = Number.parseFloat(css.getPropertyValue('--puzzle-tray-scroll-max-h'));
    const minPx = Number.isFinite(minCss) && minCss > 0 ? Math.floor(minCss) : 80;
    const viewportCap = Math.floor(typeof window !== 'undefined' ? window.innerHeight * 0.32 : 320);
    const maxPxFromCss = Number.isFinite(maxCss) && maxCss > 0 ? Math.floor(maxCss) : viewportCap;
    const maxPx = Math.max(minPx, Math.min(maxPxFromCss, viewportCap));
    const h = Math.max(minPx, Math.min(Math.ceil(pixels), maxPx));
    el.style.height = `${h}px`;
    return h;
  },

  /** Возвращает rects topbar и tray в координатах viewport (для вписывания картинки в safe area). */
  getPuzzleLayoutRects(): { topbar: DOMRect; tray: DOMRect; boardWrap: DOMRect | null } | null {
    const root = getRoot();
    const topbarEl = root.querySelector('[data-puzzle-topbar="1"]');
    const boardWrapEl = root.querySelector('[data-puzzle-board-wrap="1"]');
    const trayEl = root.querySelector('[data-puzzle-tray="1"]');
    if (!topbarEl || !trayEl) return null;
    return {
      topbar: topbarEl.getBoundingClientRect(),
      boardWrap: boardWrapEl ? boardWrapEl.getBoundingClientRect() : null,
      tray: trayEl.getBoundingClientRect(),
    };
  },

  setPuzzleZoomPercent(percent: number): void {
    const label = document.querySelector<HTMLElement>('[data-puzzle-zoom-label="1"]');
    if (!label) return;
    const safePercent = Math.max(100, Math.round(percent));
    label.textContent = `${safePercent}%`;
  },

  /**
   * Режим верхней панели пазла: normal (Назад + Прогресс + Монеты) или tutorial (Назад + Прогресс + Пропустить).
   */
  setTopBarMode(
    mode: 'normal' | 'tutorial',
    options?: { onSkip?: () => void }
  ): void {
    const root = getRoot();
    const backEl = root.querySelector<HTMLElement>('[data-puzzle-topbar-back="1"]');
    const logoEl = root.querySelector<HTMLElement>('[data-puzzle-topbar-logo="1"]');
    const coinsEl = root.querySelector<HTMLElement>('[data-puzzle-topbar-coins="1"]');
    const zoomEl = root.querySelector<HTMLElement>('[data-puzzle-topbar-zoom="1"]');
    const skipEl = root.querySelector<HTMLElement>('[data-puzzle-topbar-skip="1"]');
    if (!coinsEl || !skipEl) return;

    if (mode === 'tutorial') {
      if (backEl) backEl.style.display = 'none';
      if (logoEl) logoEl.style.display = '';
      coinsEl.style.display = 'none';
      if (zoomEl) zoomEl.style.display = 'none';
      skipEl.replaceWith(skipEl.cloneNode(true));
      const newSkip = root.querySelector<HTMLElement>('[data-puzzle-topbar-skip="1"]');
      if (newSkip) {
        newSkip.style.display = '';
        if (options?.onSkip) {
          newSkip.addEventListener('click', options.onSkip);
        }
      }
    } else {
      if (backEl) backEl.style.display = '';
      if (logoEl) logoEl.style.display = 'none';
      coinsEl.style.display = '';
      if (zoomEl) zoomEl.style.display = '';
      skipEl.style.display = 'none';
    }
  },

  /** UX-скелет игрового экрана пазла: верхняя панель, прогресс, подсказки, лоток. Без геймплея. */
  showPuzzleGame(
    data: { coins: number; pieceCount: number },
    callbacks: { onBack: () => void }
  ): void {
    const root = getRoot();
    root.innerHTML = '';

    const screen = document.createElement('div');
    screen.className = 'ui-screen ui-puzzle-game';
    screen.style.cssText =
      'position:absolute;inset:0;display:flex;flex-direction:column;align-items:stretch;max-width:100%;pointer-events:none';
    const content = document.createElement('div');
    content.style.cssText = 'pointer-events:auto;display:flex;flex-direction:column;align-items:center;gap:0.5rem;padding:0.5rem;max-width:420px;margin:0 auto;width:100%';

    const topBar = document.createElement('div');
    topBar.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:0.5rem;width:100%';
    const btnBack = createIconButton({
      ariaLabel: 'Назад',
      iconOrText: '←',
      onClick: callbacks.onBack,
    });
    topBar.appendChild(btnBack);
    const coinsCapsule = createPill({
      icon: '💎',
      text: String(data.coins),
      ariaLabel: 'Монеты',
      iconClass: 'puzzle-coins-icon',
      textClass: 'puzzle-coins',
    });
    coinsCapsule.classList.add('puzzle-coins-capsule');
    topBar.appendChild(coinsCapsule);
    content.appendChild(topBar);

    const progressEl = document.createElement('p');
    progressEl.className = 'ui-coins';
    progressEl.style.margin = '0';
    progressEl.textContent = `0 / ${data.pieceCount}`;
    content.appendChild(progressEl);

    const spacer = document.createElement('div');
    spacer.style.flex = '1';
    screen.appendChild(content);
    screen.appendChild(spacer);

    const trayWrap = document.createElement('div');
    trayWrap.style.cssText = 'pointer-events:auto;padding:0.5rem;max-width:420px;margin:0 auto;width:100%';
    const trayBox = document.createElement('div');
    trayBox.style.cssText = 'min-height:80px;background:rgba(0,0,0,0.2);border-radius:18px;border:1px dashed rgba(255,255,255,0.12)';
    trayWrap.appendChild(trayBox);
    screen.appendChild(trayWrap);

    root.appendChild(screen);
  },

  /**
   * Экран завершения пазла: «Пазл собран», «Добавлено в коллекцию», кнопки.
   * hasNextInPack: показывать кнопку «Следующий пазл» (источник pack).
   */
  showPuzzleComplete(
    data: {
      source: 'daily' | 'pack' | 'collection' | 'tutorial';
      hasNextInPack: boolean;
      boardRectPx?: { left: number; top: number; width: number; height: number } | null;
    },
    callbacks: {
      onGoToCollection: () => void;
      onGoToHub: () => void;
      onNextPuzzle?: () => void;
    }
  ): void {
    const root = getRoot();
    root.innerHTML = '';

    const buttons: Array<{ text: string; primary: boolean; onClick: () => void }> = [
      { text: 'В коллекцию', primary: true, onClick: callbacks.onGoToCollection },
      { text: 'На главный', primary: false, onClick: callbacks.onGoToHub },
    ];
    if (data.source === 'pack' && data.hasNextInPack && callbacks.onNextPuzzle) {
      buttons.push({ text: 'Следующий пазл', primary: false, onClick: callbacks.onNextPuzzle });
    }

    const wrapper = createOverlayPanel({
      dimRect: data.boardRectPx ?? undefined,
      title: 'Пазл собран!',
      subtitle: 'Теперь он в вашей коллекции.',
      buttons,
    });
    wrapper.classList.add('ui-screen', 'ui-complete-screen');
    root.appendChild(wrapper);

    const dim = wrapper.querySelector('.ui-complete-dim')!;
    const panel = wrapper.querySelector('.ui-complete-panel')!;
    requestAnimationFrame(() => {
      dim.classList.add('is-visible');
      panel.classList.add('is-visible');
    });
  },

  /**
   * Общая модалка действий по пазлу: «Собрать/Пересобрать/Продолжить» + «Отмена».
   * Использует те же классы dim/panel/buttons, что и completion overlay.
   */
  showPuzzleActionsModal(
    options: {
      title: string;
      mode?: 'start' | 'continue' | 'difficulty';
      startLabel: string;
      continueLabel?: string;
      cancelLabel?: string;
      difficultyOptions?: number[];
      subtitle?: string;
    },
    callbacks: {
      onStart: () => void;
      onContinue: () => void;
      onCancel: () => void;
      onSelectDifficulty?: (difficulty: number) => void;
    }
  ): void {
    const root = getRoot();
    const mode = options.mode ?? 'start';
    const closeWithCancel = (): void => {
      soundManager.playClose();
      wrapper.remove();
      callbacks.onCancel();
    };

    const buttons =
      mode === 'difficulty'
        ? [
            ...(options.difficultyOptions ?? []).map((difficulty) => ({
              text: formatPieceCountLabel(difficulty),
              primary: true,
              className: 'ui-difficulty-option-btn',
              onClick: () => {
                soundManager.playConfirm();
                wrapper.remove();
                callbacks.onSelectDifficulty?.(difficulty);
              },
            })),
            {
              text: options.cancelLabel ?? 'Отмена',
              primary: false,
              className: 'ui-difficulty-cancel-btn',
              onClick: closeWithCancel,
            },
          ]
        : mode === 'continue'
          ? [
            {
              text: options.continueLabel ?? 'Продолжить',
              primary: true,
              onClick: () => {
                soundManager.playConfirm();
                wrapper.remove();
                callbacks.onContinue();
              },
            },
            {
              text: options.cancelLabel ?? 'Отмена',
              primary: false,
              onClick: closeWithCancel,
            },
          ]
          : [
              {
                text: options.startLabel,
                primary: true,
                onClick: () => {
                  soundManager.playConfirm();
                  wrapper.remove();
                  callbacks.onStart();
                },
              },
              {
                text: options.cancelLabel ?? 'Отмена',
                primary: false,
                onClick: closeWithCancel,
              },
            ];

    const wrapper = createOverlayPanel({
      title: options.title,
      subtitle:
        mode === 'difficulty'
          ? (options.subtitle ?? 'Выберите сложность (кол-во деталей)')
          : undefined,
      buttons,
    });
    wrapper.dataset.puzzleActionsModal = '1';
    wrapper.classList.add('ui-screen', 'ui-complete-screen');
    if (mode === 'difficulty') {
      const actionsEl = wrapper.querySelector('.ui-complete-actions');
      actionsEl?.classList.add('ui-puzzle-actions-difficulty');
      const panelEl = wrapper.querySelector('.ui-complete-panel');
      panelEl?.classList.add('ui-puzzle-actions-panel');
    }
    const dimEl = wrapper.querySelector('.ui-complete-dim') as HTMLElement;
    dimEl.style.pointerEvents = 'auto';
    dimEl.addEventListener('click', (e) => {
      if (e.target === dimEl) {
        closeWithCancel();
      }
    });
    root.appendChild(wrapper);
    soundManager.playOpen();

    const dim = wrapper.querySelector('.ui-complete-dim')!;
    const panel = wrapper.querySelector('.ui-complete-panel')!;
    requestAnimationFrame(() => {
      dim.classList.add('is-visible');
      panel.classList.add('is-visible');
    });
  },

  /** Совместимость: Collection использует обёртку над общей модалкой. */
  showCollectionPuzzleActionsModal(
    puzzleTitle: string,
    hasSession: boolean,
    callbacks: {
      onRebuild: () => void;
      onContinue: () => void;
      onCancel: () => void;
    }
  ): void {
    this.showPuzzleActionsModal(
      {
        title: puzzleTitle,
        mode: hasSession ? 'continue' : 'start',
        startLabel: 'Пересобрать',
        continueLabel: 'Продолжить',
        cancelLabel: 'Отмена',
      },
      {
        onStart: callbacks.onRebuild,
        onContinue: callbacks.onContinue,
        onCancel: callbacks.onCancel,
      }
    );
  },

  /** Данные для экрана пазла (совместимо с GameState.getPuzzleView()). */
  showPuzzleDebug(
    data: {
      activePuzzle: {
        sourceLabel: string;
        puzzleId: string;
        difficulty: number;
        startedAt: number;
      } | null;
    },
    callbacks: { onBackToHub: () => void; onClearProgress: () => void }
  ): void {
    const root = getRoot();
    root.innerHTML = '';

    const screen = document.createElement('div');
    screen.className = 'ui-screen';

    const title = document.createElement('h1');
    title.className = 'ui-title';
    title.textContent = 'ПАЗЛ';
    screen.appendChild(title);

    const blockTitle = document.createElement('h2');
    blockTitle.className = 'ui-subtitle';
    blockTitle.textContent = 'Текущий пазл';
    screen.appendChild(blockTitle);

    if (data.activePuzzle) {
      const ap = data.activePuzzle;
      const lines = [
        `Источник: ${ap.sourceLabel}`,
        `ID: ${ap.puzzleId}`,
        `Сложность: ${ap.difficulty}`,
        `Начат: ${new Date(ap.startedAt).toLocaleString('ru')}`,
      ];
      lines.forEach((text) => {
        const p = document.createElement('p');
        p.className = 'ui-coins';
        p.textContent = text;
        screen.appendChild(p);
      });
    } else {
      const empty = document.createElement('p');
      empty.className = 'ui-coins';
      empty.textContent = 'Нет активного пазла';
      screen.appendChild(empty);
    }

    const btnBack = document.createElement('button');
    btnBack.className = 'ui-btn';
    btnBack.textContent = 'Назад в хаб';
    btnBack.type = 'button';
    btnBack.addEventListener('click', callbacks.onBackToHub);
    screen.appendChild(btnBack);

    const btnClear = document.createElement('button');
    btnClear.className = 'ui-btn';
    btnClear.textContent = 'Очистить прогресс';
    btnClear.type = 'button';
    btnClear.addEventListener('click', callbacks.onClearProgress);
    screen.appendChild(btnClear);

    root.appendChild(screen);
  },

  /** Экран коллекции: стандартный хедер (назад + алмазы), заголовок, сетка карточек. */
  showCollection(
    data: {
      collectedItems: Array<{ key: string; imageUrl: string; title: string }>;
      unlockProgress: {
        nextDifficulty: 252 | 448 | 1200 | null;
        current: number;
        target: number | null;
      };
      coins: number;
    },
    callbacks: {
      onBackToHub: () => void;
      onRebuild: (key: string) => void;
      onCardClick: (key: string, title: string) => void;
    }
  ): void {
    const root = getRoot();
    root.innerHTML = '';

    const screen = document.createElement('div');
    screen.className = 'ui-screen collection-screen';
    screen.style.overflowY = 'auto';
    screen.style.maxHeight = '100vh';

    // Header: same as Hub/Puzzle — back left, diamonds pill right (reuse classes, no new CSS)
    const header = document.createElement('div');
    header.className = 'hub-topbar';
    header.style.width = '100%';
    header.style.flexShrink = '0';

    const btnBack = createIconButton({
      ariaLabel: 'Назад',
      iconOrText: '←',
      onClick: callbacks.onBackToHub,
    });
    btnBack.className = 'ui-btn ui-btn-back';
    header.appendChild(btnBack);

    const spacer = document.createElement('div');
    spacer.style.flex = '1';
    spacer.style.minWidth = '8px';
    header.appendChild(spacer);

    const coinsPill = createPill({
      icon: '💎',
      text: String(data.coins),
      ariaLabel: 'Монеты',
      iconClass: 'puzzle-coins-icon',
      textClass: 'puzzle-coins',
    });
    coinsPill.classList.add('puzzle-coins-capsule');
    header.appendChild(coinsPill);

    screen.appendChild(header);

    // Content: title → subtitle → divider → grid
    const content = document.createElement('div');
    content.className = 'collection-content';

    const titleEl = document.createElement('h1');
    titleEl.className = 'collection-title';
    titleEl.textContent = 'Моя коллекция';
    content.appendChild(titleEl);

    const subtitleEl = document.createElement('p');
    subtitleEl.className = 'collection-subtitle';
    subtitleEl.textContent = `Собрано: ${data.collectedItems.length}`;
    content.appendChild(subtitleEl);

    const divider = document.createElement('div');
    divider.className = 'collection-divider';
    content.appendChild(divider);

    const grid = document.createElement('div');
    grid.className = 'collection-grid';

    data.collectedItems.forEach((item) => {
      const card = document.createElement('div');
      card.className = 'collection-card';
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');

      if (item.imageUrl) {
        const thumb = createLazyImage({
          src: item.imageUrl,
          alt: item.title,
          loadImmediately: false,
        });
        const thumbWrap = document.createElement('div');
        thumbWrap.className = 'collection-card-thumb';
        ;(thumb as HTMLElement).style.width = '100%';
        ;(thumb as HTMLElement).style.height = '100%';
        ;(thumb as HTMLElement).style.objectFit = 'cover';
        thumbWrap.appendChild(thumb);
        card.appendChild(thumbWrap);
      }

      const nameEl = document.createElement('span');
      nameEl.className = 'collection-card-title';
      nameEl.textContent = item.title;
      card.appendChild(nameEl);

      card.addEventListener('click', () => {
        soundManager.playClick();
        callbacks.onCardClick(item.key, item.title);
      });

      grid.appendChild(card);
    });

    content.appendChild(grid);
    screen.appendChild(content);
    setupLazyImages(screen);

    root.appendChild(screen);
  },

  /** Показать экран «Загрузка...» до загрузки контента (Preload). */
  showLoading(): void {
    const root = getRoot();
    root.innerHTML = '';

    const screen = document.createElement('div');
    screen.className = 'ui-screen ui-loading-screen';

    const panel = document.createElement('div');
    panel.className = 'ui-loading';

    const brand = document.createElement('div');
    brand.className = 'ui-loading-brand';

    const logo = document.createElement('img');
    logo.className = 'ui-loading-logo';
    logo.src = '/assets/img/logo_with_text.png';
    logo.alt = '';
    logo.setAttribute('aria-hidden', 'true');

    const logoFallback = document.createElement('div');
    logoFallback.className = 'ui-loading-logo-fallback';
    logoFallback.setAttribute('aria-hidden', 'true');
    logoFallback.textContent = '🧩';
    logoFallback.style.display = 'none';

    logo.addEventListener('error', () => {
      logo.style.display = 'none';
      logoFallback.style.display = '';
    });

    brand.appendChild(logo);
    brand.appendChild(logoFallback);
    panel.appendChild(brand);

    const text = document.createElement('p');
    text.className = 'ui-loading-text';
    text.textContent = 'Загрузка...';
    panel.appendChild(text);

    // Резерв под реальный прогресс preloader (пока — бесконечная анимация).
    const progress = document.createElement('div');
    progress.className = 'ui-loading-progress';
    const progressBar = document.createElement('div');
    progressBar.className = 'ui-loading-progress-bar';
    progress.appendChild(progressBar);
    panel.appendChild(progress);

    screen.appendChild(panel);

    root.appendChild(screen);
  },

  /**
   * Экран «Все подборки»: тот же хедер что у Collection, блок заголовка, сетка подборок.
   */
  showPacksList(
    data: { packs: HubViewData['packs']; coins: number },
    callbacks: { onBack: () => void; onOpenPack: (packId: string) => void }
  ): void {
    const { packs } = data;
    const root = getRoot();
    root.innerHTML = '';

    const screen = document.createElement('div');
    screen.className = 'ui-screen collection-screen';
    screen.style.overflowY = 'auto';
    screen.style.maxHeight = '100vh';

    // Header: same as Collection — back left, diamonds pill right
    const header = document.createElement('div');
    header.className = 'hub-topbar';
    header.style.width = '100%';
    header.style.flexShrink = '0';

    const btnBack = createIconButton({
      ariaLabel: 'Назад',
      iconOrText: '←',
      onClick: callbacks.onBack,
    });
    btnBack.className = 'ui-btn ui-btn-back';
    header.appendChild(btnBack);

    const spacer = document.createElement('div');
    spacer.style.flex = '1';
    spacer.style.minWidth = '8px';
    header.appendChild(spacer);

    const coinsPill = createPill({
      icon: '💎',
      text: String(data.coins),
      ariaLabel: 'Монеты',
      iconClass: 'puzzle-coins-icon',
      textClass: 'puzzle-coins',
    });
    coinsPill.classList.add('puzzle-coins-capsule');
    header.appendChild(coinsPill);

    screen.appendChild(header);

    // Content: title → subtitle → divider → grid (reuse Collection block classes)
    const content = document.createElement('div');
    content.className = 'collection-content';

    const titleEl = document.createElement('h1');
    titleEl.className = 'collection-title';
    titleEl.textContent = 'Подборки';
    content.appendChild(titleEl);

    const subtitleEl = document.createElement('p');
    subtitleEl.className = 'collection-subtitle';
    subtitleEl.textContent = `Всего: ${packs.length}`;
    content.appendChild(subtitleEl);

    const divider = document.createElement('div');
    divider.className = 'collection-divider';
    content.appendChild(divider);

    const packsGrid = document.createElement('div');
    packsGrid.id = 'packs-list-grid';
    packsGrid.style.cssText = `display:grid;gap:${PACKS_GRID_GAP_PX}px;width:100%`;
    packs.forEach((pack) => {
      const preview = pack.firstPuzzleImageUrl
        ? createLazyImage({
            src: pack.firstPuzzleImageUrl,
            alt: pack.title,
            className: 'ui-pack-preview',
            loadImmediately: false,
          })
        : null;
      if (preview) {
        (preview as HTMLElement).style.width = '100%';
        (preview as HTMLElement).style.height = '100%';
        const img = (preview as HTMLElement).querySelector('img');
        if (img) img.style.objectFit = 'cover';
      }
      const card = createPackCard({
        preview,
        titleText: pack.title,
        progressCollected: pack.collectedInPack,
        progressTotal: pack.total,
        onClick: () => callbacks.onOpenPack(pack.packId),
        isPurchased: pack.isPurchased,
        lockOverlayText: pack.isPurchased ? undefined : '1 пазл бесплатно',
        rewardBadge: !pack.isPurchased && pack.hasRewardedPuzzle ? 'Ad' : undefined,
      });
      packsGrid.appendChild(card);
    });
    content.appendChild(packsGrid);
    screen.appendChild(content);

    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.alignItems = 'center';
    root.style.minHeight = '100%';
    root.appendChild(screen);
    setupLazyImages(content);

    requestAnimationFrame(() => {
      const w = packsGrid.getBoundingClientRect().width;
      const cardW = (w - PACKS_GRID_GAP_PX) / PACKS_GRID_COLS;
      const cardH = cardW * PACKS_GRID_CARD_HEIGHT_RATIO;
      packsGrid.style.gridTemplateColumns = `repeat(${PACKS_GRID_COLS}, ${cardW}px)`;
      packsGrid.style.gridAutoRows = `${cardH}px`;
    });
  },

  hide(): void {
    if (hubPacksGridResizeObserver) {
      hubPacksGridResizeObserver.disconnect();
      hubPacksGridResizeObserver = null;
    }
    const root = getRoot();
    root.innerHTML = '';
    root.style.display = '';
    root.style.flexDirection = '';
    root.style.alignItems = '';
    root.style.justifyContent = '';
    root.style.minHeight = '';
  },
};
